/**
 * VIP Program Service
 * Handles VIP tier management, benefits configuration, and member portal
 */
import api from './api';

const vipService = {
  // ==================== VIP Program Status ====================
  /**
   * Get VIP program status
   */
  getStatus: async () => {
    const response = await api.get('/marketing/vip/status');
    return response.data;
  },

  // ==================== Tiers ====================
  /**
   * Get all VIP tiers
   */
  getTiers: async () => {
    const response = await api.get('/marketing/vip/tiers');
    return response.data;
  },

  /**
   * Configure VIP tier structure
   */
  configureTiers: async (tierConfig) => {
    const response = await api.post('/marketing/vip/configure-tiers', tierConfig);
    return response.data;
  },

  /**
   * Configure tier benefits
   */
  configureBenefits: async (benefitsConfig) => {
    const response = await api.post('/marketing/vip/configure-benefits', benefitsConfig);
    return response.data;
  },

  // ==================== VIP Customers ====================
  /**
   * Get all VIP customers
   */
  getCustomers: async (params = {}) => {
    const response = await api.get('/marketing/vip/customers', { params });
    return response.data;
  },

  /**
   * Get VIP customer details
   */
  getCustomer: async (customerId) => {
    const response = await api.get(`/marketing/vip/customers/${customerId}`);
    return response.data;
  },

  /**
   * Add customer to VIP program
   */
  addCustomer: async (customerId, tierId) => {
    const response = await api.post('/marketing/vip/customers', {
      customer_id: customerId,
      tier_id: tierId
    });
    return response.data;
  },

  /**
   * Update customer tier
   */
  updateCustomerTier: async (customerId, tierId, reason = '') => {
    const response = await api.put(`/marketing/vip/customers/${customerId}/tier`, {
      tier_id: tierId,
      reason
    });
    return response.data;
  },

  /**
   * Remove customer from VIP program
   */
  removeCustomer: async (customerId) => {
    const response = await api.delete(`/marketing/vip/customers/${customerId}`);
    return response.data;
  },

  // ==================== Tier Tracking ====================
  /**
   * Implement tier tracking
   */
  implementTierTracking: async (trackingConfig) => {
    const response = await api.post('/marketing/vip/tier-tracking', trackingConfig);
    return response.data;
  },

  /**
   * Get tier progress for customer
   */
  getCustomerProgress: async (customerId) => {
    const response = await api.get(`/marketing/vip/customers/${customerId}/progress`);
    return response.data;
  },

  // ==================== Member Portal ====================
  /**
   * Build VIP member portal configuration
   */
  buildMemberPortal: async (portalConfig) => {
    const response = await api.post('/marketing/vip/member-portal', portalConfig);
    return response.data;
  },

  /**
   * Get member portal settings
   */
  getMemberPortalSettings: async () => {
    const response = await api.get('/marketing/vip/member-portal/settings');
    return response.data;
  },

  // ==================== Program Launch ====================
  /**
   * Launch VIP program
   */
  launchProgram: async (launchConfig) => {
    const response = await api.post('/marketing/vip/launch-program', launchConfig);
    return response.data;
  },

  // ==================== Analytics ====================
  /**
   * Get VIP program analytics
   */
  getAnalytics: async (params = {}) => {
    const response = await api.get('/marketing/vip/analytics', { params });
    return response.data;
  },

  /**
   * Get tier distribution
   */
  getTierDistribution: async () => {
    const response = await api.get('/marketing/vip/analytics/tier-distribution');
    return response.data;
  },

  /**
   * Get benefit utilization
   */
  getBenefitUtilization: async (params = {}) => {
    const response = await api.get('/marketing/vip/analytics/benefit-utilization', { params });
    return response.data;
  }
};

export default vipService;
